import { Controller } from "@hotwired/stimulus"

// Per-metric column toggles and per-row toggles for the conversion-pin matrix. Each metric column
// (Conv / Value / All Conv / All Value) has a header checkbox that checks/unchecks that metric for
// every row; each conversion-action row has a leading checkbox that checks/unchecks every metric for
// that row. Both reflect all/some/none (checked / indeterminate) from their cells.
//
// Perf: sync tallies cells in ONE pass over cellTargets, rather than re-reading this.cellTargets
// (a DOM re-scan) once per toggle — matrices with many conversion actions have hundreds of cells and
// dozens of toggles, so the per-toggle re-scan made large lists laggy.
//
// Markup contract (see reports/_column_picker):
//   data-controller="reporting--conversion-matrix"
//   header:   <input data-reporting--conversion-matrix-target="columnToggle"
//                    data-metric="conversions" data-action="change->...#toggleColumn">
//   rowToggle:<input data-reporting--conversion-matrix-target="rowToggle"
//                    data-row="<escaped-name>" data-action="change->...#toggleRow">
//   cell:     <input data-reporting--conversion-matrix-target="cell" data-metric="conversions"
//                    data-row="<escaped-name>" value="<escaped-name>:conversions"
//                    data-action="change->...#cellChanged">
export default class extends Controller {
  static targets = ["columnToggle", "rowToggle", "cell"]

  connect() {
    this.syncToggles()
  }

  // Header checkbox clicked → match every cell in that metric column to it.
  toggleColumn(event) {
    const toggle = event.currentTarget
    const metric = toggle.dataset.metric
    this.cellTargets.forEach((cell) => {
      if (cell.dataset.metric === metric) cell.checked = toggle.checked
    })
    this.syncToggles()
  }

  // Row checkbox clicked → match every metric cell in that row to it.
  toggleRow(event) {
    const toggle = event.currentTarget
    const row = toggle.dataset.row
    this.cellTargets.forEach((cell) => {
      if (cell.dataset.row === row) cell.checked = toggle.checked
    })
    this.syncToggles()
  }

  // A single cell changed → refresh its column + row all/some/none state.
  cellChanged() {
    this.syncToggles()
  }

  // One pass over the cells, tallying per metric and per row, then reflect each toggle.
  syncToggles() {
    const byMetric = {}
    const byRow = {}

    this.cellTargets.forEach((cell) => {
      const metricCount = (byMetric[cell.dataset.metric] ||= { total: 0, checked: 0 })
      const rowCount = (byRow[cell.dataset.row] ||= { total: 0, checked: 0 })
      metricCount.total++
      rowCount.total++
      if (cell.checked) {
        metricCount.checked++
        rowCount.checked++
      }
    })

    this.columnToggleTargets.forEach((toggle) => this.reflect(toggle, byMetric[toggle.dataset.metric]))
    this.rowToggleTargets.forEach((toggle) => this.reflect(toggle, byRow[toggle.dataset.row]))
  }

  reflect(toggle, counts) {
    const total = counts ? counts.total : 0
    const checked = counts ? counts.checked : 0
    toggle.checked = checked > 0 && checked === total
    toggle.indeterminate = checked > 0 && checked < total
  }
}
